'use client';

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { getStorageRaw, setStorageItem, STORAGE_KEYS } from '@/core/utils/local-storage';

interface SidebarContextType {
  isCollapsed: boolean;
  toggleSidebar: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

/**
 * Improved Sidebar Provider
 * 
 * Key improvements:
 * - No timer memory leaks
 * - Cleaner state management
 * - Persists preference to localStorage
 * - Responsive to screen size
 */
export function SidebarProvider({ children }: { children: ReactNode }) {
  // Always initialize to false for SSR to match server-rendered HTML
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  // Hydrate client-side state after mount (prevents hydration mismatch)
  useEffect(() => {
    // Check localStorage first
    const stored = getStorageRaw(STORAGE_KEYS.SIDEBAR_COLLAPSED);
    if (stored !== null) {
      queueMicrotask(() => {
        setIsCollapsed(stored === 'true');
      });
    } else {
      // Default based on screen size if no stored preference
      queueMicrotask(() => {
        setIsCollapsed(window.innerWidth < 1024);
      });
    }
    queueMicrotask(() => {
      setIsHydrated(true);
    });
  }, []);

  // Toggle sidebar state
  const toggleSidebar = useCallback(() => {
    setIsCollapsed(prev => {
      const newState = !prev;
      // Persist to localStorage
      if (typeof window !== 'undefined') {
        setStorageItem(STORAGE_KEYS.SIDEBAR_COLLAPSED, String(newState));
      }
      return newState;
    });
  }, []);

  // Handle responsive behavior
  useEffect(() => {
    if (!isHydrated) return; // Wait for hydration

    const handleResize = () => {
      // Only auto-collapse on small screens if user hasn't set preference
      const stored = getStorageRaw(STORAGE_KEYS.SIDEBAR_COLLAPSED);
      if (stored === null && window.innerWidth < 1024) {
        queueMicrotask(() => {
          setIsCollapsed(true);
        });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isHydrated]);
  
  return (
    <SidebarContext.Provider value={{ isCollapsed, toggleSidebar }}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar(): SidebarContextType {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}